import { WIZARD_API } from '@/features/walkthrough/demo/wizardApi';

/**
 * Apply the `actions` of an `AssistantTurnResponse` to the wizard through the
 * `WizardApiProvider` registry (the same imperative APIs the demo tour drives).
 *
 * Action types mirror `backend/app/services/assistant/turn.py`. `escalate` is
 * handled by the hook (it needs the transcript), so it is skipped here. A
 * missing registry entry (page not mounted) logs a warning and is skipped —
 * the assistant never throws into the wizard.
 */

export const ASSISTANT_ACTION = Object.freeze({
    setForm: 'set_form',
    createUsecase: 'create_usecase',
    goToStep: 'go_to_step',
    pickFiles: 'pick_files',
    openDataSource: 'open_data_source',
    patchDetails: 'patch_details',
    dataCollectionNext: 'data_collection_next',
    selectTeacher: 'select_teacher',
    setSystemPrompt: 'set_system_prompt',
    setUserPrompt: 'set_user_prompt',
    modelSetupNext: 'model_setup_next',
    escalate: 'escalate',
});

/**
 * `open_data_source` payload values (`backend/app/services/assistant/copy.py`
 * `DATA_SOURCE_*`); the Data Collection panel's `openDataSource(source)` maps
 * them onto the "Need a Dataset?" entry points.
 */
export const DATA_SOURCE = Object.freeze({
    generateAi: 'generate_ai',
    retrievalAgent: 'retrieval_agent',
    huggingFace: 'huggingface',
});

/** Option ids that open the native file dialog (`backend/app/services/assistant/copy.py`). */
export const UPLOAD_DATASET_OPTION_ID = 'upload_dataset';
export const UPLOAD_BENCHMARK_OPTION_ID = 'upload_benchmark';

export const isUploadOption = (optionId) =>
    optionId === UPLOAD_DATASET_OPTION_ID || optionId === UPLOAD_BENCHMARK_OPTION_ID;

/**
 * How long to wait for the window to blur before treating `input.click()` as
 * a no-op. A click after an `await` is no longer a user gesture, so the
 * browser drops the dialog and never fires `change` or `cancel`. Waiting on
 * that promise froze every later chat turn.
 */
export const FILE_PICKER_OPEN_PROBE_MS = 1000;

/** Default file picker: a hidden `<input type="file">` resolved with the chosen files. */
export function openNativeFilePicker({
    accept,
    multiple = true,
    probeMs = FILE_PICKER_OPEN_PROBE_MS,
} = {}) {
    return new Promise((resolve) => {
        if (typeof document === 'undefined') {
            resolve([]);
            return;
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = multiple;
        if (accept) input.accept = accept;
        input.style.display = 'none';
        input.setAttribute('data-testid', 'spark-assistant-file-input');
        let settled = false;
        let sawBlur = false;
        let probeTimer = 0;
        let focusTimer = 0;
        const finish = (files) => {
            if (settled) return;
            settled = true;
            clearTimeout(probeTimer);
            clearTimeout(focusTimer);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
            input.remove();
            resolve(Array.from(files || []));
        };
        // `change` / `cancel` fire before `focus` when a real dialog closes.
        // If focus returns and neither did, the dialog was dismissed.
        const onFocus = () => {
            focusTimer = window.setTimeout(() => finish([]), 0);
        };
        const onBlur = () => {
            sawBlur = true;
            clearTimeout(probeTimer);
            window.addEventListener('focus', onFocus);
        };
        input.addEventListener('change', () => finish(input.files));
        input.addEventListener('cancel', () => finish([]));
        window.addEventListener('blur', onBlur);
        // No blur means the dialog never opened. Resolve so the chat can move on.
        probeTimer = window.setTimeout(() => {
            if (!sawBlur) finish([]);
        }, probeMs);
        document.body.appendChild(input);
        input.click();
    });
}

function warnMissing(action, name) {
    console.warn(`[spark-assistant] cannot apply "${action.type}": "${name}" api is not mounted`);
}

/**
 * @param {Array<{type: string, payload?: object}>} actions
 * @param {{ get: (name: string) => object | null } | null} registry `useWizardApiRegistry()`
 * @param {{ pickFiles?: (opts: {accept?: string}) => Promise<File[]> }} [options]
 *   `pickFiles` overrides the native picker (tests, e2e).
 * @returns {Promise<string[]>} the action types that were applied
 */
export async function applyAssistantActions(actions, registry, options = {}) {
    const applied = [];
    if (!Array.isArray(actions) || !actions.length) return applied;
    if (!registry) {
        console.warn('[spark-assistant] no wizard api registry; actions skipped');
        return applied;
    }
    const pickFiles = options.pickFiles || openNativeFilePicker;

    for (const action of actions) {
        if (!action || typeof action.type !== 'string') continue;
        const payload = action.payload || {};
        try {
            switch (action.type) {
                case ASSISTANT_ACTION.setForm: {
                    const wizard = registry.get(WIZARD_API.wizard);
                    if (!wizard?.setForm) {
                        warnMissing(action, WIZARD_API.wizard);
                        break;
                    }
                    wizard.setForm(payload);
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.createUsecase: {
                    const wizard = registry.get(WIZARD_API.wizard);
                    if (!wizard?.createUsecase) {
                        warnMissing(action, WIZARD_API.wizard);
                        break;
                    }
                    await wizard.createUsecase();
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.goToStep: {
                    const wizard = registry.get(WIZARD_API.wizard);
                    if (!wizard?.goToStep) {
                        warnMissing(action, WIZARD_API.wizard);
                        break;
                    }
                    wizard.goToStep(payload.step, payload.usecase_id);
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.pickFiles: {
                    const dataCollection = registry.get(WIZARD_API.dataCollection);
                    if (!dataCollection?.pickFiles) {
                        warnMissing(action, WIZARD_API.dataCollection);
                        break;
                    }
                    const files = await pickFiles({ accept: dataCollection.accept });
                    if (files.length) {
                        await dataCollection.pickFiles(payload.target || 'dataset', files);
                    }
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.openDataSource: {
                    const dataCollection = registry.get(WIZARD_API.dataCollection);
                    if (!dataCollection?.openDataSource) {
                        warnMissing(action, WIZARD_API.dataCollection);
                        break;
                    }
                    // `false` means the card has no such entry point for this
                    // task type; the backend already said so, nothing to apply.
                    if (dataCollection.openDataSource(payload.source) !== false) {
                        applied.push(action.type);
                    }
                    break;
                }
                case ASSISTANT_ACTION.patchDetails: {
                    const dataCollection = registry.get(WIZARD_API.dataCollection);
                    if (!dataCollection?.patchDetails) {
                        warnMissing(action, WIZARD_API.dataCollection);
                        break;
                    }
                    const target =
                        !payload.target || payload.target === 'dataset'
                            ? dataCollection.primaryTarget || 'dataset'
                            : payload.target;
                    dataCollection.patchDetails(target, payload.patch || {});
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.dataCollectionNext: {
                    const dataCollection = registry.get(WIZARD_API.dataCollection);
                    if (!dataCollection?.next) {
                        warnMissing(action, WIZARD_API.dataCollection);
                        break;
                    }
                    await dataCollection.next();
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.selectTeacher: {
                    const modelSetup = registry.get(WIZARD_API.modelSetup);
                    if (!modelSetup?.selectTeacher) {
                        warnMissing(action, WIZARD_API.modelSetup);
                        break;
                    }
                    modelSetup.selectTeacher({ provider: payload.provider, model: payload.model });
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.setSystemPrompt: {
                    const modelSetup = registry.get(WIZARD_API.modelSetup);
                    if (!modelSetup?.setSystemPrompt) {
                        warnMissing(action, WIZARD_API.modelSetup);
                        break;
                    }
                    modelSetup.setSystemPrompt(payload.text ?? '');
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.setUserPrompt: {
                    const modelSetup = registry.get(WIZARD_API.modelSetup);
                    if (!modelSetup?.setUserPrompt) {
                        warnMissing(action, WIZARD_API.modelSetup);
                        break;
                    }
                    modelSetup.setUserPrompt(payload.text ?? '');
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.modelSetupNext: {
                    const step = registry.get(WIZARD_API.modelSetupStep);
                    if (!step?.next) {
                        warnMissing(action, WIZARD_API.modelSetupStep);
                        break;
                    }
                    await step.next();
                    applied.push(action.type);
                    break;
                }
                case ASSISTANT_ACTION.escalate:
                    // Needs the transcript — `useSparkAssistant` handles it.
                    break;
                default:
                    console.warn(`[spark-assistant] unknown action type "${action.type}"`);
            }
        } catch (err) {
            console.warn(`[spark-assistant] action "${action.type}" failed`, err);
        }
    }
    return applied;
}
